import type { FlowError, LoginFlow, RecoveryFlow, RegistrationFlow, SettingsFlow, VerificationFlow } from "@ory/client-fetch";
import { Alert, Button, Card, CardBody, CardHeader } from "@payezy/ui";
import { useEffect, useState, type ReactNode } from "react";
import { AuthShell, FlowBody, FlowLoading, useFlow } from "./FlowPage";
import type { Kratos } from "./kratos";

type PageProps = { kratos: Kratos; brand: ReactNode; links?: { login?: string; registration?: string; recovery?: string; verification?: string } };

function query(name: string): string | undefined {
  return new URLSearchParams(window.location.search).get(name) ?? undefined;
}

export function LoginPage({ kratos, brand, links, mfaSetupUrl }: PageProps & { mfaSetupUrl?: string }) {
  const aal = query("aal");
  const refresh = query("refresh");
  const returnTo = query("return_to");
  const { flow, error } = useFlow<LoginFlow>(kratos, "login", { aal, refresh, return_to: returnTo });
  const isAAL2 = flow?.requested_aal === "aal2";
  const unverified = flow?.ui.messages?.some((m) => m.id === 4000010);
  // Session is AAL1 and the identity has no second factor yet: Kratos still opens an aal2
  // login flow, but with nothing to submit. Send them to settings (must be outside any
  // AAL2-gated app shell, otherwise setup loops back here).
  const noSecondFactor = isAAL2 && flow && !flow.ui.nodes.some((n) => ["totp", "lookup_secret", "passkey", "webauthn", "code"].includes(n.group));
  const setupHref = mfaSetupUrl
    ? `${mfaSetupUrl}${mfaSetupUrl.includes("?") ? "&" : "?"}return_to=${encodeURIComponent("/auth/login?aal=aal2")}`
    : undefined;
  return (
    <AuthShell
      brand={brand}
      footer={
        !isAAL2 && (
          <div className="flex flex-col gap-1">
            {links?.registration && (
              <span>
                New here?{" "}
                <a className="text-brand-600 hover:underline" href={links.registration + (returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "")}>
                  Create an account
                </a>
              </span>
            )}
            {links?.recovery && (
              <a className="text-brand-600 hover:underline" href={links.recovery}>Forgot your password?</a>
            )}
          </div>
        )
      }
    >
      <h1 className="mb-1 text-lg font-semibold">{isAAL2 ? (noSecondFactor ? "Set up two-factor authentication" : "Confirm it's you") : flow?.refresh ? "Re-enter your password" : "Sign in"}</h1>
      <p className="mb-5 text-sm text-ink-500">
        {isAAL2
          ? noSecondFactor
            ? "This account requires a passkey or authenticator app before you can continue."
            : "Use your second factor to continue with this sensitive action."
          : "Welcome back."}
      </p>
      {noSecondFactor ? (
        <Alert tone="warning" title="Two-factor authentication required">
          Enroll a passkey or authenticator app, then you will be asked to confirm it once.
          {setupHref && (
            <div className="mt-3">
              <a href={setupHref}><Button size="sm">Set up two-factor</Button></a>
            </div>
          )}
        </Alert>
      ) : flow ? (
        <>
          <FlowBody ui={flow.ui} />
          {unverified && links?.verification && (
            <p className="mt-4 text-sm">
              <a className="text-brand-600 hover:underline" href={links.verification}>Resend verification email</a>
            </p>
          )}
        </>
      ) : (
        <FlowLoading error={error} />
      )}
    </AuthShell>
  );
}

export function RegistrationPage({ kratos, brand, links }: PageProps) {
  const { flow, error } = useFlow<RegistrationFlow>(kratos, "registration", { return_to: query("return_to") });
  return (
    <AuthShell
      brand={brand}
      footer={links?.login && <span>Already have an account? <a className="text-brand-600 hover:underline" href={links.login}>Sign in</a></span>}
    >
      <h1 className="mb-1 text-lg font-semibold">Create your account</h1>
      <p className="mb-5 text-sm text-ink-500">Open a personal or business account in minutes.</p>
      {flow ? <FlowBody ui={flow.ui} /> : <FlowLoading error={error} />}
    </AuthShell>
  );
}

export function RecoveryPage({ kratos, brand, links }: PageProps) {
  const { flow, error } = useFlow<RecoveryFlow>(kratos, "recovery");
  return (
    <AuthShell brand={brand} footer={links?.login && <a className="text-brand-600 hover:underline" href={links.login}>Back to sign in</a>}>
      <h1 className="mb-1 text-lg font-semibold">Recover your account</h1>
      <p className="mb-5 text-sm text-ink-500">We'll email you a one-time code.</p>
      {flow ? <FlowBody ui={flow.ui} /> : <FlowLoading error={error} />}
    </AuthShell>
  );
}

export function VerificationPage({ kratos, brand, links }: PageProps) {
  const { flow, error } = useFlow<VerificationFlow>(kratos, "verification", { return_to: query("return_to") });
  return (
    <AuthShell brand={brand} footer={links?.login && <a className="text-brand-600 hover:underline" href={links.login}>Back to sign in</a>}>
      <h1 className="mb-1 text-lg font-semibold">Verify your email</h1>
      <p className="mb-5 text-sm text-ink-500">Enter the code we sent to your inbox.</p>
      {flow ? <FlowBody ui={flow.ui} /> : <FlowLoading error={error} />}
    </AuthShell>
  );
}

export function ErrorPage({ kratos, brand, homeUrl }: { kratos: Kratos; brand: ReactNode; homeUrl: string }) {
  const [err, setErr] = useState<FlowError | null>(null);
  const id = query("id");
  useEffect(() => {
    if (id) kratos.api.getFlowError({ id }).then(setErr).catch(() => setErr(null));
  }, [id, kratos]);
  const message = (err?.error as { message?: string; reason?: string } | undefined)?.reason ?? (err?.error as { message?: string } | undefined)?.message;
  return (
    <AuthShell brand={brand}>
      <h1 className="mb-3 text-lg font-semibold">Something went wrong</h1>
      <Alert tone="danger">{message ?? "An unexpected error occurred."}</Alert>
      <a href={homeUrl} className="mt-4 inline-block"><Button variant="secondary">Go home</Button></a>
    </AuthShell>
  );
}

const settingsSections: { group: string; title: string; description: string }[] = [
  { group: "profile", title: "Profile", description: "Your name and email address." },
  { group: "password", title: "Password", description: "Change the password you use to sign in." },
  { group: "passkey", title: "Passkeys", description: "Sign in with Face ID, Touch ID, or a hardware key." },
  { group: "totp", title: "Authenticator app", description: "Use an app like 1Password or Google Authenticator for two-factor codes." },
  { group: "lookup_secret", title: "Backup codes", description: "One-time codes for when you can't use your other factors." },
];

/** Security settings: one Kratos form per method, as Kratos requires. */
export function SettingsSections({ kratos, returnTo }: { kratos: Kratos; returnTo?: string }) {
  const { flow, error } = useFlow<SettingsFlow>(kratos, "settings", { return_to: returnTo });
  if (!flow) return <FlowLoading error={error} />;
  const present = new Set<string>(flow.ui.nodes.map((n) => n.group));
  return (
    <div className="flex flex-col gap-4">
      {flow.ui.messages?.length ? <FlowBody ui={{ ...flow.ui, nodes: [] }} /> : null}
      {settingsSections
        .filter((s) => present.has(s.group))
        .map((s) => (
          <Card key={s.group}>
            <CardHeader title={s.title} description={s.description} />
            <CardBody>
              <FlowBody ui={{ ...flow.ui, messages: [] }} groups={[s.group]} />
            </CardBody>
          </Card>
        ))}
    </div>
  );
}

/**
 * Standalone MFA / security settings page used outside the authenticated app shell.
 * Staff first-time enrollment must live here: the back office API requires AAL2, so a
 * settings link inside the shell would bounce straight back to the AAL2 login screen.
 */
export function SettingsPage({ kratos, brand, title, description }: { kratos: Kratos; brand: ReactNode; title?: string; description?: string }) {
  const returnTo = query("return_to") ?? "/";
  return (
    <div className="flex min-h-dvh flex-col items-center px-4 py-12">
      <div className="mb-8 text-xl font-bold tracking-tight">{brand}</div>
      <div className="w-full max-w-xl">
        <h1 className="mb-1 text-lg font-semibold">{title ?? "Passkeys & MFA"}</h1>
        <p className="mb-5 text-sm text-ink-500">
          {description ?? "Enroll a passkey or authenticator app. After saving, confirm the new factor to finish signing in."}
        </p>
        <SettingsSections kratos={kratos} returnTo={returnTo} />
        <p className="mt-6 text-center text-sm text-ink-500">
          <a className="text-brand-600 hover:underline" href={returnTo.startsWith("/") ? returnTo : "/"}>Continue</a>
        </p>
      </div>
    </div>
  );
}
